import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiGatewayHelperService } from 'modules/app/services/apigateway-helper.service';
import { BehaviorSubject, Subject } from 'rxjs';

export interface AssignmentData {
  startDate: Date;
  title: string;
  dueDate: Date | string;
  created: boolean;
  isContentLocked: boolean;
}
export interface AssignmentDataParams {
  title: string;
  duedate: number;
  startdate: number;
  shared_progress: boolean;
  items: Item[];
  context: {
      space_key: string;
      productcode: string;
      'bundle-code': string;
  };
  rules?: {
    rule_id: string;
  };
}
export interface EditAssignmentDataParams {
  context: {
    space_key: string;
    productcode: string;
    'bundle-code': string;
  };
  title?: string;
  duedate?: number;
  startdate?: number;
  items?: Item[];
  scope?: string;
  isSkipNotification?: boolean;
  isCustomNotifEventNeeded?: boolean;
}

export interface Item {
  'item-code': string;
  'dls_product_code': string;
  item_type: string;
}

export interface FlattenProductItem {
  [key: string]: boolean
}

@Injectable()
export class AssignmentViewService {
    constructor(
        private http: HttpClient,
        private apiGatewayHelperService: ApiGatewayHelperService
    ) {}

    public updateClassDetailsEvent = new Subject<any>();
    public deletedAssignmentSubject = new Subject<object>();
    private _assignmentCreatedData: AssignmentData = this.__getInitialAssignmentData();
    public currentClassLearnersData: any;
    private _totalAssignmentCount: number = 0; 
    /* Contains not assigned items of current component */
    private _currentComponentItemsNotAssigned: FlattenProductItem;
    /* Contains all the assigned assignments items within bundle */
    private _itemsAlreadyAssignedInClass: FlattenProductItem;
    /** Flag to check if existing assignment is initialized */
    public isExistingAssignmentInitialized: boolean = false;
    /** Contains existing assignment data */
    private _existingAssignmentData: any = {};
    
    /**
     * Contains all the assigment navigation events
     * We have made this subject as behavior subject so 
     * that we can get the last emitted value when component is destroyed on navigation
     * */
    public assignmentCreationState = new BehaviorSubject<{
      isAssignDetailCompShown: boolean;
      isEditMode?: boolean;
      resetToc?: boolean;
      showSuccessBanner?: boolean; // Flag to show success banner in case of Assignment creation journey only
      // Flag to navigate to assignment summary page in edit assignment journey and assignment detail page 
      // (from activity selection) in create assignment journey
      navigateToAssignmentSummaryOrDetailsPage?: boolean;
    }>({
      isAssignDetailCompShown: false,
      isEditMode: false,
      resetToc: false,
      showSuccessBanner: false,
      navigateToAssignmentSummaryOrDetailsPage: false
    });

    //getter setter for assignment detail data
    set assignmentCreatedData(data: AssignmentData) {
      this._assignmentCreatedData = data;
    }

    get assignmentCreatedData(): AssignmentData {
      return this._assignmentCreatedData;
    }

    //getter setter for assignment detail data
    set existingAssignmentData(data) {
      this._existingAssignmentData = data;
    }

    get existingAssignmentData() {
      return this._existingAssignmentData;
    }

    //getter setter for assignment's total count
    set totalAssignmentCount(data: number) {
      this._totalAssignmentCount = data;
    }

    get totalAssignmentCount(): number {
      return this._totalAssignmentCount;
    }

    //getter setter for _currentComponentItemsNotAssigned
    set currentComponentItemsNotAssigned(data: FlattenProductItem) {
      this._currentComponentItemsNotAssigned = data;
    }

    get currentComponentItemsNotAssigned(): FlattenProductItem {
      return this._currentComponentItemsNotAssigned;
    }

    //getter setter for _itemsAlreadyAssignedInClass
    set itemsAlreadyAssignedInClass(data: FlattenProductItem) {
      this._itemsAlreadyAssignedInClass = data;
    }

    get itemsAlreadyAssignedInClass(): FlattenProductItem {
      return this._itemsAlreadyAssignedInClass;
    }

    createAssignment(spaceId, classId, assignmentData, flattenedJSONUrlMap, assignedLearners?, accessRuleData?) {
      return this.http.post(this.apiGatewayHelperService.getApiGatewayPrefixUrl() + spaceId + '/class/' + classId + '/create-assignment', { assignmentData, flattenedJSONUrlMap, assignedLearners, accessRuleData });
    }

    updateAssignment(spaceId, classId, assignedPathId, usersToEnrollInAssignedPath, usersToUnEnrollFromAssignedPath, assignmentData, accessRuleParams, newNotifyUsers) {
      return this.http.post(this.apiGatewayHelperService.getApiGatewayPrefixUrl() + spaceId + '/class/' + classId + '/update-assignment' + '/' + assignedPathId, { assignmentData, usersToEnrollInAssignedPath, usersToUnEnrollFromAssignedPath, accessRuleParams, newNotifyUsers });
    }


    public resetAssignmentData() {
      this.assignmentCreatedData = this.__getInitialAssignmentData();
    }

    public updateTotalAssignCountByOne() {
      ++this.totalAssignmentCount;
    }

    public editClass(classId, editClassData) {
      return this.http.put(this.apiGatewayHelperService.getApiGatewayPrefixUrl(true)  + 'class/' + classId + '/edit', editClassData);
    }

    getAssignableComponents(classDetails, isMQASchool) {
        return classDetails.products.filter(product => {
          const bundleCodes = product.meta['bundle-codes'];
          if (!bundleCodes) {
            return;
          }
        
          const classBundles = classDetails.bundles[bundleCodes[0]];
          if (!classBundles) {
            return;
          }
        
          const bundledComponent = classBundles.products.find(digitalComponent => {
            if(digitalComponent.meta && digitalComponent.meta.visibility === "coming-soon" && !isMQASchool){
              return;
            }
            if (digitalComponent['dls-product'] 
            && digitalComponent['dls-product'].code === product.productcode) {
              return digitalComponent;
            }
          });

          // do not count this bundle as assignable component as it is suppressed.
          if(this.isProductSuppressed(bundledComponent, isMQASchool)) {
            return;
          }

          if (bundledComponent) {
            return !bundledComponent.assignability || bundledComponent.assignability.enabled;
          }
        });
    }

    isProductSuppressed(bundledComponent, isMQASchool) {


      // check if org is mqa. if true show irrespective of visibility flag.
      // check if visibility is true, display if true.
      // product is hidden when org is non mqa and visibility is false.
      let isHidden = !isMQASchool && bundledComponent && bundledComponent['meta'] && bundledComponent['meta']['visibility'] == 'false';

      // NEMO-6677 : suppress products with visibility hidden or visibility options false.
      if (isHidden || bundledComponent && bundledComponent['meta'] && bundledComponent['meta']['visibility-options'] && bundledComponent['meta']['visibility-options']['hide-assignments']) {
        return true;
      }
      return false;

    }

    public getFlattenedJsonUrls(productTocData, productCode) {
      const isMQASchool = productTocData.orgId.indexOf('mqa') === 0;
      const assignableComponents = this.getAssignableComponents(productTocData.classmeta, isMQASchool);
      let flattenedJSONMap = {};
      assignableComponents.forEach(product => {
        if (product['productcode'] !== productCode) {
          flattenedJSONMap[product['productcode']] = product.meta.paths['json-flattened'];
        }
      });
      return flattenedJSONMap;
    }

    private __getInitialAssignmentData(): AssignmentData { 
      return {
        startDate: new Date(),
        title: '',
        dueDate: '',
        created: false,
        isContentLocked: false
      };

    }
}